import { type ThunkAction, type ThunkDispatch } from "@reduxjs/toolkit";
import {
  primaryLocationFromRestaurant,
  toDraftRestaurant,
} from "dashboard/src/models/locations";
import type { Unsubscribe } from "firebase/firestore";

import type {
  NonPrimaryLocation,
  PrimaryLocation,
  RestaurantLocation,
} from "@dashboard/types/location";
import { type ReduxState } from "@dashboard/types/reduxState";
import { type ThunkExtraArgument } from "@dashboard/types/thunk";

import { type ErrorReducerAction } from "../reducers/errorReducer";
import type { LocationReducerAction } from "../reducers/locationReducer";
import type { RestaurantReducerAction } from "../reducers/restaurantReducer";
import { addError } from "./addErrorAction";

// Types
type LocationAction =
  | LocationReducerAction
  | RestaurantReducerAction
  | ErrorReducerAction;

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  LocationAction
>;

type LocationSaveThunkAction = ThunkAction<
  Promise<boolean>,
  ReduxState,
  ThunkExtraArgument,
  LocationAction
>;

export type LocationThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  LocationAction
>;

// Actions
export const newLocation: (
  location: NonPrimaryLocation
) => LocationSaveThunkAction = (location: NonPrimaryLocation) => {
  return async (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi }: ThunkExtraArgument
  ) => {
    try {
      await locationApi.newLocation(location);
      return true;
    } catch (error) {
      addError(dispatch)(error as Error | string);
      return false;
    }
  };
};

export const updateLocation: (
  location: RestaurantLocation
) => LocationSaveThunkAction = (location: RestaurantLocation) => {
  return async (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi, restaurantApi }: ThunkExtraArgument
  ) => {
    try {
      if (location.isPrimary) {
        // Call restaurantApi directly with the location's restaurantUuid
        // to avoid reading selectedRestaurant from state, which can change
        // if the user switches restaurants before the save completes.
        const draftRestaurant = toDraftRestaurant(location);
        await restaurantApi.updateRestaurantDetails(
          location.restaurantUuid,
          draftRestaurant
        );
        // No listener covers the restaurant document, so the restaurant and
        // the primary location derived from it are both updated here.
        dispatch({
          type: "RESTAURANT_DETAILS_UPDATED",
          payload: draftRestaurant,
        });
        dispatch({ type: "PRIMARY_LOCATION_UPDATED", payload: location });
      } else {
        await locationApi.updateLocation(location);
      }
      return true;
    } catch (error) {
      addError(dispatch)(error as Error | string);
      return false;
    }
  };
};

export const deleteLocation: (
  restaurantUuid: string,
  locationId: string
) => ThunkActionReturn = (restaurantUuid: string, locationId: string) => {
  return (
    dispatch: LocationThunkDispatch,
    getState: () => ReduxState,
    { locationApi }: ThunkExtraArgument
  ) => {
    return locationApi
      .deleteLocation(restaurantUuid, locationId)
      .catch(addError(dispatch));
  };
};

// Live listener: returns the unsubscribe synchronously (not a promise).
// The collection holds the non primary locations only, so each emit is
// combined with the primary location derived from the current restaurant.
export const subscribeToLocations =
  (
    restaurantUuid: string
  ): ThunkAction<Unsubscribe, ReduxState, ThunkExtraArgument, LocationAction> =>
  (dispatch, getState, { locationApi }) =>
    locationApi.subscribeToLocations(
      restaurantUuid,
      (nonPrimaryLocations) => {
        const restaurant = getState().restaurant.selectedRestaurant!;
        dispatch({
          type: "FETCHED_LOCATIONS",
          payload: [
            primaryLocationFromRestaurant(restaurant, nonPrimaryLocations),
            ...nonPrimaryLocations,
          ],
        });
      },
      addError(dispatch)
    );
